import React from 'react';
import { Box, Button, Tooltip } from '@mui/material';
import ArrowDropUpRounded from '@mui/icons-material/ArrowDropUpRounded';
import { bool, func, node, oneOfType, string } from 'prop-types';
import palette from 'library/theme/figma/palette';
import { useCustomizablePopover } from './useCustomizablePopover';

const secondaryStates = palette.light.secondary._states;
const { secondary } = palette.light;

const ActionFooter = ({
  buttonSpacing,
  disabled,
  handleReset,
  popoverContent,
  primaryActionButtonTitle,
  secondaryActionButtonTitle,
}) => {
  const secondaryStateBg = palette.light.secondary._states.background;

  const { Popover, handlePopoverOpen, handlePopoverClose, openPopover } = useCustomizablePopover();

  const wrappedPopoverContent =
    typeof popoverContent === 'function' ? popoverContent({ handlePopoverClose }) : popoverContent;

  const enabledButton = (
    <Button
      aria-describedby={openPopover ? 'bulk-actions-popover' : undefined}
      endIcon={
        <ArrowDropUpRounded
          sx={{
            fontSize: '20px',
            height: '20px',
            width: '20px',
          }}
        />
      }
      fullWidth
      onClick={handlePopoverOpen}
      sx={{
        // Icon - same color as text
        '& .MuiButton-endIcon': {
          color: secondary.main,
        },
        // Hover state
        '&:hover': {
          backgroundColor: secondaryStates.background,
          borderColor: secondary.main,
        },
        // Default state
        borderColor: secondary.main,
        color: secondary.main,
        display: 'flex',
        gap: '8px',
        justifyContent: 'center',
        typography: 'button',
        width: '150px',
        ...(openPopover && {
          '& .MuiButton-endIcon': {
            color: 'secondary.dark',
          },
          '&:hover': {
            backgroundColor: secondaryStates.background,
            borderColor: secondary.main,
          },
          backgroundColor: secondaryStates.background,
          borderColor: secondary.main,
          color: secondary.main,
        }),
      }}
      variant="outlined"
    >
      {primaryActionButtonTitle}
    </Button>
  );

  const disabledButton = (
    <Button
      disabled
      endIcon={<ArrowDropUpRounded />}
      fullWidth
      sx={{
        '&.Mui-disabled': {
          borderColor: 'action.disabledBackground',
          color: 'action.disabled',
        },
        '&.Mui-disabled .MuiButton-endIcon': {
          color: 'action.disabled',
        },
        typography: 'button',
        width: '156px',
      }}
      variant="outlined"
    >
      {primaryActionButtonTitle}
    </Button>
  );

  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        boxShadow:
          '0 1px 5px 0 rgba(0, 0, 0, 0.12), 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 3px 1px -2px rgba(0, 0, 0, 0.20)',
        display: 'flex',
        gap: 1,
        justifyContent: buttonSpacing,
        mt: 'auto',
        pl: 2,
        position: 'relative',
        pr: 4,
        py: 2,
        zIndex: 1,
      }}
    >
      {secondaryActionButtonTitle && (
        <Button
          disabled={disabled}
          fullWidth
          onClick={handleReset}
          sx={{
            '&:hover': {
              backgroundColor: secondaryStateBg,
            },
            borderColor: 'secondary.main',
            color: 'secondary.dark',
            typography: 'button',
            width: '156px',
          }}
          variant="text"
        >
          {secondaryActionButtonTitle}
        </Button>
      )}

      {disabled ? (
        <Tooltip
          title={
            <span style={{ whiteSpace: 'pre-line' }}>
              Select one or more items to perform
              {'\n'}a bulk action.
            </span>
          }
        >
          <span>{disabledButton}</span>
        </Tooltip>
      ) : (
        enabledButton
      )}
      <Popover id="bulk-actions-popover" popoverMainContent={wrappedPopoverContent} />
    </Box>
  );
};

ActionFooter.propTypes = {
  buttonSpacing: string.isRequired,
  disabled: bool,
  handleReset: func,
  popoverContent: oneOfType([node, func]),
  primaryActionButtonTitle: string.isRequired,
  secondaryActionButtonTitle: string,
};

ActionFooter.defaultProps = {
  disabled: false,
  handleReset: () => {},
  popoverContent: null,
  secondaryActionButtonTitle: '',
};

export default ActionFooter;
